import { useEffect, useState } from 'react'
import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  PlayCircleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { AdminApi, AdminCourseComment } from '../../services/adminApi'
import { toast } from 'react-toastify'

type CommentFilter = 'all' | 'answered' | 'unanswered'

type Props = {
  courseId: number
  onOpenComment: (courseContentId: number, commentId: number) => void
}

const formatDate = (dateString: string) => {
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return dateString
  return new Intl.DateTimeFormat('vi-VN', { dateStyle: 'medium', timeStyle: 'short' }).format(d)
}

const AdminCourseCommentsTab = ({ courseId, onOpenComment }: Props) => {
  const [filter, setFilter] = useState<CommentFilter>('all')
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<AdminCourseComment[]>([])
  const [summary, setSummary] = useState({ all: 0, answered: 0, unanswered: 0 })

  const fetchComments = async (status: CommentFilter) => {
    if (!courseId) return
    setLoading(true)
    try {
      const res = await AdminApi.getCourseComments(courseId, status)
      if (res.success) {
        setItems(res.data.items)
        setSummary(res.data.summary)
      }
    } catch (error) {
      console.error('Failed to fetch course comments', error)
      toast.error('Lỗi khi tải danh sách bình luận')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchComments(filter)
  }, [courseId, filter])

  const filters: { key: CommentFilter; label: string; count: number }[] = [
    { key: 'all', label: 'Tất cả', count: summary.all },
    { key: 'unanswered', label: 'Chưa trả lời', count: summary.unanswered },
    { key: 'answered', label: 'Đã trả lời', count: summary.answered },
  ]

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 shrink-0">
        <div>
          <h3 className="text-lg font-bold text-m2-on-surface">Bình luận học viên</h3>
          <p className="text-sm text-m2-on-surface-variant mt-0.5">
            Click vào bình luận để mở bài học và trả lời tại chỗ.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium border transition-colors ${
                filter === f.key
                  ? f.key === 'unanswered'
                    ? 'bg-amber-50 text-amber-800 border-amber-300'
                    : f.key === 'answered'
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                      : 'bg-m2-primary/10 text-m2-primary border-m2-primary/30'
                  : 'bg-m2-surface-container text-m2-on-surface-variant border-m2-outline-variant hover:bg-m2-surface-high'
              }`}
            >
              {f.label}
              <span className="text-xs opacity-80">({f.count})</span>
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-m2-outline-variant bg-m2-surface-container">
        {loading ? (
          <div className="flex h-48 items-center justify-center text-m2-on-surface-variant">
            Đang tải bình luận...
          </div>
        ) : items.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-m2-on-surface-variant px-6 text-center">
            <ChatBubbleLeftRightIcon className="h-14 w-14 mb-3 text-m2-outline-variant" />
            <p className="text-base">
              {filter === 'unanswered'
                ? 'Không còn bình luận nào chưa trả lời.'
                : filter === 'answered'
                  ? 'Chưa có bình luận nào đã được trả lời.'
                  : 'Khóa học này chưa có bình luận nào.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-m2-outline-variant">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onOpenComment(item.courseContentId, item.id)}
                  className="w-full text-left px-4 py-4 hover:bg-m2-primary/5 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <span className="font-semibold text-m2-on-surface text-sm">{item.userName}</span>
                        {item.isAnswered ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                            <CheckCircleIcon className="h-3.5 w-3.5" />
                            Đã trả lời
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                            <ClockIcon className="h-3.5 w-3.5" />
                            Chưa trả lời
                          </span>
                        )}
                        <span className="text-xs text-m2-on-surface-variant">
                          {formatDate(item.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-m2-on-surface line-clamp-2 whitespace-pre-wrap mb-2">
                        {item.commentText}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-m2-on-surface-variant">
                        {String(item.contentType).toUpperCase() === 'VIDEO' ? (
                          <PlayCircleIcon className="h-4 w-4 text-red-500" />
                        ) : (
                          <DocumentTextIcon className="h-4 w-4 text-blue-500" />
                        )}
                        <span className="truncate max-w-[28rem]">{item.contentName}</span>
                        {item.replyCount > 0 && (
                          <span className="text-m2-outline">· {item.replyCount} phản hồi</span>
                        )}
                      </div>
                    </div>
                    <span className="shrink-0 text-xs font-medium text-m2-primary mt-1">
                      Xem bài học →
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default AdminCourseCommentsTab
